import * as React from "react";
import { AlertMessage, AlertType, ButtonType, CustomButton, CustomSubmitButton, DatePickerRange, Dropdown, Form, HiddenInput, Modal, ModalBody, ModalFooter, ModalSize, TextInput } from "soprasteria-ui-library";
import { useAuth } from "../../App";
import { CarePerformerApi } from "../../api/CarePerformerApi";
import { setMessage } from "../../helpers/MessageHelper";
import { ICarePerformer, ISaveCarePerformer } from "../../models/ICarePerformer";
import { IClinic } from "../../models/IClinic";
import { ClinicApi } from "../../api/ClinicApi";
import { nameOf } from "../../helpers/PropertyNameHelper";
import { UserCompetenceApi } from "../../api/UserCompetenceApi";
import { IUserCompetence, IUserCompetenceSearchParams } from "../../models/IUserCompetence";
import { UserApi } from "../../api/UserApi";
import { formatDate, getDateFromDefaultValueToday } from "../../helpers/DateHelper";
import { IUser } from "../../models/IUser";

interface IProps {
    carePerformer?: ICarePerformer;
    clinicId: number;
    closeModal: () => void;
    onSubmit: (message: string) => void;
}

interface ISearchableUser {
    id: number;
    name: string;
    hsaId: string;
}

export const CarePerformerModal: React.FunctionComponent<IProps> = ({ carePerformer, clinicId, closeModal, onSubmit }) => {
    const auth = useAuth();
    const api = new CarePerformerApi(auth);
    const clinicApi = new ClinicApi(auth);
    const userApi = new UserApi(auth);
    const userCompetenceApi = new UserCompetenceApi(auth);
    const abortController = new AbortController();

    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectableUsers, setSelectableUsers] = React.useState<ISearchableUser[]>();
    const [selectedClinic, setSelectedClinic] = React.useState<IClinic>();
    const [selectedUser, setSelectedUser] = React.useState<IUser>();
    const [userCompetence, setUserCompetence] = React.useState<IUserCompetence>();
    const [hasUserCompetence, setHasUserCompetence] = React.useState<boolean>();
    const [dateToday] = React.useState<Date>(new Date());

    React.useEffect(() => {
        document.getElementById("open-care-performer-modal")?.click();
        checkIfUserHasUserCompetence(carePerformer?.userId, carePerformer?.dateFrom, carePerformer?.dateTo);

        if (carePerformer === undefined) {
            loadClinic();
        }

        return () => {
            abortController.abort();
        }
    }, [carePerformer]);

    React.useEffect(() => {
        if (selectedClinic) {
            loadSelectableUsers(selectedClinic.organisationId);
        }

    }, [selectedClinic]);

    const loadClinic = () => {
        setIsLoading(true);

        clinicApi.getClinicByClinicId(clinicId, (data) => {
            setSelectedClinic(data);
        }, undefined, () => setIsLoading(false));
    }

    const loadSelectableUsers = (organisationId: number) => {
        setIsLoading(true);

        api.getCarePerformerUsersByOrganisationId(organisationId, (data) => {
            let mappedUsers = data.map<ISearchableUser>((user) => { return { id: user.userId, name: user.name, hsaId: user.hsaId } });
            setSelectableUsers(mappedUsers);
        }, () => setIsLoading(false));
    }

    const loadSelectedUser = (userId?: number) => {
        if (userId) {
            setIsLoading(true);
            userApi.getUserByUserId(userId, (data) => {
                setSelectedUser(data);
                checkIfUserHasUserCompetence(data.userId, getDateFromDefaultValueToday(), undefined);
            }, undefined, () => setIsLoading(false));
        }
    }

    const checkIfUserHasUserCompetence = (userId?: number, dateFrom?: Date, dateTo?: Date) => {
        if (userId) {
            setIsLoading(true);
            const searchParams = { searchType: 0, userId: userId, dateFrom: dateFrom, dateTo: dateTo, pageSize: 0, pageNumber: 0, propertyToOrderBy: "", sortOrder: 0 };
            userCompetenceApi.hasUserCompetenceByUserIdAndDates(searchParams, (data) => {
                setHasUserCompetence(data);
                if (data) {
                    loadUserCompetence(searchParams);
                }
            }, () => setIsLoading(false));
        }
    }

    const loadUserCompetence = (searchParams: IUserCompetenceSearchParams) => {
        userCompetenceApi.getUserCompetenceByUserIdAndDates(searchParams, (data) => {
            setUserCompetence(data);
        }, undefined);
    }

    const onFormSubmit = (data: ISaveCarePerformer) => {
        setIsLoading(true);
        if (carePerformer) {
            api.updateCarePerformer(data, () => {
                onSubmit("Behandlare har redigerats");
            }, (message: string) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
            }, () => setIsLoading(false));
        } else {
            api.createCarePerformer(data, () => {
                onSubmit("Behandlare har lagts till");
            }, (message: string) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
            }, () => setIsLoading(false));
        }
    }

    const getUserItemLabel = (user: ISearchableUser) => {
        return user.hsaId ? `${user.hsaId} - ${user.name}` : user.name;
    }

    const onSelectedUserChange = (userId: string) => {
        loadSelectedUser(parseInt(userId));
    }

    return (
        <Modal id="care-performer-modal" modalSize={ModalSize.large} header={carePerformer ? "Redigera behandlare" : "Lägg till behandlare"}>
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={5000} >{errorMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-12">
                            {
                                carePerformer ?
                                    <>
                                        <HiddenInput name={nameOf<ISaveCarePerformer>('carePerformerId')} value={carePerformer.carePerformerId.toString()} />
                                        <HiddenInput name={nameOf<ISaveCarePerformer>('clinicId')} value={carePerformer.clinicId.toString()} />
                                        <HiddenInput name={nameOf<ISaveCarePerformer>('userId')} value={carePerformer.userId.toString()} />
                                        <TextInput labelCol={4} inputCol={8} label="Användare" name="readonly" defaultValue={carePerformer.userFullName} readonly inlineLabel />
                                        <DatePickerRange label="Fr.o.m. - t.o.m."
                                            labelCol={4}
                                            inputCol={8}
                                            name={nameOf<ISaveCarePerformer>('dateFrom')}
                                            value={new Date(carePerformer.dateFrom)}
                                            valueSecondary={carePerformer.dateTo ? new Date(carePerformer.dateTo) : undefined}
                                            nameSecondary={nameOf<ISaveCarePerformer>('dateTo')}
                                            requiredFrom
                                            inlineLabel />
                                    </> : selectedClinic && selectableUsers ?
                                        <>
                                            <HiddenInput name={nameOf<ISaveCarePerformer>('clinicId')} value={selectedClinic.clinicId.toString()} />
                                            <Dropdown name={nameOf<ISaveCarePerformer>('userId')} label="Användare" clearValueIfNoInitalValue items={selectableUsers} getItemLabel={getUserItemLabel} placeholder="Välj en användare" onValueChange={onSelectedUserChange} noOptionsMessage="Det finns inga valbara användare" defaultValue={undefined} errorMessage="Välj en användare" isLoading={isLoading} isClearable={true} required />
                                            <DatePickerRange label="Fr.o.m. - t.o.m."
                                                labelCol={4}
                                                inputCol={8}
                                                name={nameOf<ISaveCarePerformer>('dateFrom')}
                                                value={dateToday}
                                                nameSecondary={nameOf<ISaveCarePerformer>('dateTo')}
                                                requiredFrom
                                                inlineLabel />
                                        </> : null
                            }
                            {
                                userCompetence &&
                                <>
                                    <hr />
                                    <h5>Senaste kompetens inom perioden {formatDate(carePerformer?.dateFrom ? carePerformer?.dateFrom : new Date())} - {carePerformer?.dateTo ? formatDate(carePerformer?.dateTo) : "Tillsvidare"}</h5>
                                </>
                            }
                            {
                                userCompetence ?
                                    <TextInput labelCol={4} inputCol={8} label="Legitimation" name="readonly" defaultValue={userCompetence?.careLicenseTypeName} readonly inlineLabel />
                                    : null
                            }
                            {
                                userCompetence?.hasS9Qualification && userCompetence?.hasS9Qualification === true ?
                                    <TextInput labelCol={4} inputCol={8} label="Kompetens inom S9" name="readonly" defaultValue="Ja" readonly inlineLabel />
                                    : null
                            }
                            {
                                userCompetence?.hasS10Qualification && userCompetence?.hasS10Qualification === true ?
                                    <TextInput labelCol={4} inputCol={8} label="Kompetens inom S10" name="readonly" defaultValue="Ja" readonly inlineLabel />
                                    : null
                            }
                            {
                                userCompetence?.careAssignmentName ?
                                    <TextInput labelCol={4} inputCol={8} label="Bevis om specialistkompetens" name="readonly" defaultValue={userCompetence?.careAssignmentName} readonly inlineLabel />
                                    : null
                            }
                            {
                                userCompetence?.isInSpecialistTraining && userCompetence?.isInSpecialistTraining === true ?
                                    <TextInput labelCol={4} inputCol={8} label="Under specialisttjänstgöring" name="readonly" defaultValue="Ja" readonly inlineLabel />
                                    : null
                            }
                            {
                                !hasUserCompetence && selectedUser ?
                                    <p>Användaren saknar kompetens</p>
                                    : null
                            }
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-care-performer-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText="Avbryt" />
                        <CustomSubmitButton isLoading={isLoading}>Spara</CustomSubmitButton>
                        <input type="hidden" data-toggle="modal" id="open-care-performer-modal" data-target="#care-performer-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    );
}